import React, {FC} from 'react';
import styled from 'styled-components';

import {Plural} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {fontSizes} from '../../../libs/shared/ui/src/styles/fontStyles';

/*
 * Props.
 */

export interface StartGuideProgressBarProps {
  isBanner?: boolean;
  isAdminSetupGuide?: boolean;
  completedSteps: number;
  totalSteps: number;
  hasCompletedAllTasks?: boolean;
  initialProgressWidth?: string;
}

/*
 * Constants.
 */

const progressBarTransitionDelayInMs = 0;
const progressBarTransitionAnimationInMs = 1000;
export const progressBarTransitionDurationInMs =
  progressBarTransitionDelayInMs + progressBarTransitionAnimationInMs;

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 8px;
  width: 100%;
`;

const StyledBarBackground = styled.div<{$isBanner: boolean}>`
  width: 100%;
  height: ${(p) => (p.$isBanner ? '4px' : '5px')};
  background-color: ${(p) => p.theme.greys.shade30};
  border-radius: 10px;
`;

interface ProgressStyleProps {
  $progress: number;
  $hasLargerInitialProgress: boolean;
  $initialProgressWidth: string | undefined;
}
const StyledProgressDiv = styled.div<ProgressStyleProps>`
  width: ${(p) => (p.$progress === 0 ? getInitialProgressWidth(p) : `${p.$progress}%`)};
  height: 100%;
  background-color: ${(p) => p.theme.palette.green.shade40};
  border-radius: 10px;

  transition: ${(progressBarTransitionAnimationInMs / 1000).toFixed(3)}s ease;
  transition-delay: ${(progressBarTransitionDelayInMs / 1000).toFixed(3)}s;
`;

function getInitialProgressWidth(p: ProgressStyleProps) {
  if (p.$initialProgressWidth) {
    return p.$initialProgressWidth;
  }
  return p.$hasLargerInitialProgress ? '3%' : '1%';
}

const StyledProgressDescriptionContainerDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StyledDescriptionDiv = styled.div`
  font-size: ${fontSizes.medium};
  color: ${(p) => p.theme.greys.shade70};
  line-height: 16px;
`;

/*
 * Component.
 */

export const StartGuideProgressBar: FC<StartGuideProgressBarProps> = ({
  isBanner,
  isAdminSetupGuide,
  completedSteps,
  totalSteps,
  initialProgressWidth,
}) => {
  const completionPercentage = Math.floor((completedSteps / totalSteps) * 100);
  const hasLargerInitialProgress = Boolean(isBanner);

  return (
    <StyledWrapperDiv>
      <StyledBarBackground $isBanner={Boolean(isBanner)}>
        <StyledProgressDiv
          $progress={completionPercentage}
          $hasLargerInitialProgress={hasLargerInitialProgress}
          $initialProgressWidth={initialProgressWidth}
        />
      </StyledBarBackground>
      {!isBanner && !isAdminSetupGuide && (
        <StyledProgressDescriptionContainerDiv>
          <StyledDescriptionDiv>{getProgressDescription(completedSteps, totalSteps)}</StyledDescriptionDiv>
        </StyledProgressDescriptionContainerDiv>
      )}
    </StyledWrapperDiv>
  );
};

/*
 * Helpers.
 */

function getProgressDescription(completedSteps: number, totalSteps: number) {
  return (
    <Plural
      id="startguide-progressbar-description-default"
      one={`1/${totalSteps} step completed by your team`}
      other={`${completedSteps}/${totalSteps} steps completed by your team`}
      value={completedSteps}
    />
  );
}
